import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useCallback } from 'react';
import { PricingTerm } from '@/constants/pricingTypes';
import { AttributeKinds } from '@/constants/attributeTypes';
import {
  AttributeSelection,
  QuoteState,
  QuoteAttribute,
  AvailableAttribute,
  CoverageValidation,
} from '@/schemas/quoteResponseSchema';
import { updateCoverage } from '@/api/agency-coverage-api';
import useAnalytics, { eventName } from '@/hooks/use-analytics';
interface QuoteStore {
  quote: QuoteState | null;
  setQuote: (quote: QuoteState) => void;
  updateQuoteAttributes: (
    symbol: string,
    scope: string,
    updater: (attrs: QuoteAttribute[]) => QuoteAttribute[]
  ) => void;
  setCoverageValidation: (symbol: string, scope: string, validation: CoverageValidation) => void;
  updatePricingTerm: (term: PricingTerm) => void;
}

export const useQuoteStore = create<QuoteStore>()(
  devtools(
    (set) => ({
      quote: null,

      setQuote: (quote) => set({ quote }, false, 'setQuote'),

      updateQuoteAttributes: (symbol, scope, updater) =>
        set(
          (state) => {
            if (!state.quote) return state;
            const coverageIndex = state.quote.coverages.findIndex(
              (coverage) => coverage.symbol === symbol && coverage.scope === scope
            );

            const coverage = state.quote.coverages[coverageIndex];
            if (!coverage) return state;

            return {
              quote: {
                ...state.quote,
                coverages: state.quote.coverages.map((coverage, index) =>
                  index === coverageIndex
                    ? {
                        ...coverage,
                        attributes: updater(coverage?.attributes),
                      }
                    : coverage
                ),
              },
            };
          },
          false,
          'updateQuoteAttributes'
        ),

      setCoverageValidation: (symbol, scope, validation) =>
        set(
          (state) => {
            if (!state.quote) return state;
            const coverageIndex = state.quote.coverages.findIndex(
              (coverage) => coverage.symbol === symbol && coverage.scope === scope
            );

            const coverage = state.quote.coverages[coverageIndex];
            if (!coverage) return state;
            return {
              quote: {
                ...state.quote,
                coverages: state.quote.coverages.map((coverage, index) =>
                  index === coverageIndex
                    ? {
                        ...coverage,
                        validations: validation,
                      }
                    : coverage
                ),
              },
            };
          },
          false,
          'setCoverageValidation'
        ),

      updatePricingTerm: (term) =>
        set(
          (state) => {
            if (!state.quote) return state;

            return {
              quote: {
                ...state.quote,
                details: {
                  ...state.quote.details,
                  initial_term_selection: term,
                },
              },
            };
          },
          false,
          'updatePricingTerm'
        ),
    }),
    { name: 'QuoteStore' }
  )
);

export const useQuote = () => {
  return useQuoteStore((state) => state.quote);
};

const UPDATE_DEBOUNCE_MS = 300;

const globalPendingRequest = {
  timeout: null as ReturnType<typeof setTimeout> | null,
  controller: null as AbortController | null,
};

const cancelPendingRequests = () => {
  if (globalPendingRequest.timeout) {
    clearTimeout(globalPendingRequest.timeout);
    globalPendingRequest.timeout = null;
  }
  if (globalPendingRequest.controller) {
    globalPendingRequest.controller.abort();
    globalPendingRequest.controller = null;
  }
};

const isCurrentRequest = (controller: AbortController) => {
  return globalPendingRequest.controller === controller;
};

const parseErrorMessage = (error: string): string => {
  return error.split('\nHTTP status code:')[0].trim();
};

// Handles known validation errors from API only
const handleApiResponse = (
  response: QuoteState,
  symbol: string,
  scope: string,
  setCoverageValidation: (symbol: string, scope: string, validation: CoverageValidation) => void,
  setQuote: (quote: QuoteState) => void
) => {
  const currentTerm = useQuoteStore.getState().quote?.details.initial_term_selection;

  // Preserve current pricing term selection
  setQuote({
    ...response,
    details: {
      ...response.details,
      initial_term_selection: currentTerm ?? response.details.initial_term_selection,
    },
  });

  // TODO revise this rather hacky approach to showing server errors as inline validation errors
  if (response.error) {
    console.log('Validation error from API:', response.error);
    setCoverageValidation(symbol, scope, {
      valid: false,
      message: parseErrorMessage(response.error),
      requirements: [],
    });
  }
};

const useTrackAttributeChange = () => {
  const { trackEvent } = useAnalytics();

  return useCallback(
    (symbol: string, kind: string) => {
      trackEvent(`${eventName(symbol)}_${eventName(kind)}_SELECTED`);
    },
    [trackEvent]
  );
};

const useUpdateQuoteAttribute = (symbol: string, scope: string) => {
  const setQuote = useQuoteStore((state) => state.setQuote);
  const updateQuoteAttributes = useQuoteStore((state) => state.updateQuoteAttributes);
  const setCoverageValidation = useQuoteStore((state) => state.setCoverageValidation);
  const trackAttributeChange = useTrackAttributeChange();

  return useCallback(
    (kind: string, value: AttributeSelection) => {
      const quote = useQuoteStore.getState().quote;
      if (!quote) return;

      trackAttributeChange(symbol, kind);

      updateQuoteAttributes(symbol, scope, (attributes) =>
        attributes.map((attr) =>
          attr.kind === kind
            ? {
                ...attr,
                selection: { value, valid: true, message: null, requirements: [] },
              }
            : attr
        )
      );
      cancelPendingRequests();

      globalPendingRequest.timeout = setTimeout(async () => {
        const controller = new AbortController();
        globalPendingRequest.controller = controller;

        try {
          const currentQuote = useQuoteStore.getState().quote;
          if (!currentQuote) return;

          const response = await updateCoverage({ coverages: currentQuote.coverages }, { signal: controller.signal });

          if (!isCurrentRequest(controller)) return;

          handleApiResponse(response, symbol, scope, setCoverageValidation, setQuote);
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') return;

          // Handle actual request failures (network errors, timeouts, 500s, etc.)
          console.error('Request failed:', error);
          setCoverageValidation(symbol, scope, {
            valid: false,
            message: error instanceof Error ? error.message : 'An unexpected error occurred',
            requirements: [],
          });
        }
      }, UPDATE_DEBOUNCE_MS);
    },
    [updateQuoteAttributes, setQuote, setCoverageValidation, symbol, trackAttributeChange, scope]
  );
};

export const useUpdatePricingTerm = () => {
  const updatePricingTerm = useQuoteStore((state) => state.updatePricingTerm);
  return updatePricingTerm;
};

export const useQuoteAttributes = (symbol: string, apply_to: string) => {
  const quote = useQuoteStore((state) => state.quote);
  return quote?.coverages.find((coverage) => coverage.symbol === symbol && coverage.scope === apply_to);
};

export const useCoverageSection = (symbol: string, apply_to: string, available_attributes: AvailableAttribute[]) => {
  const coverage = useQuoteAttributes(symbol, apply_to);
  const handleAttributeChange = useUpdateQuoteAttribute(symbol, apply_to);

  const getCurrentValue = useCallback(
    (kind: string): AttributeSelection | undefined => {
      return coverage?.attributes.find((qa) => qa.kind === kind)?.selection?.value;
    },
    [coverage]
  );

  const declinedAttribute = available_attributes?.find((attr) => attr.kind === AttributeKinds.DECLINED);
  const declinedValue = getCurrentValue(AttributeKinds.DECLINED);

  const isDeclinedSelection = (
    val: AttributeSelection | undefined
  ): val is { description: string; declined?: boolean } => {
    return val !== undefined && 'declined' in val;
  };

  return {
    isDeclined: isDeclinedSelection(declinedValue) && declinedValue.declined === true,
    isRequired: !declinedAttribute,
    handleAttributeChange,
    getCurrentValue,
  };
};

export const useCoverageValidation = (symbol: string, apply_to: string) => {
  const coverage = useQuoteAttributes(symbol, apply_to);

  return coverage?.validation;
};
